import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSecureAccounts } from '../../store/secureAccountsSlice';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Tooltip,
  TextField,
  Chip,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Search as SearchIcon, ContentCopy as ContentCopyIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import SecureAccountAddDialog from './secureaccountAdd';

const initialForm = {
  appName: '',
  accountName: '',
  purpose: '',
  description: '',
  status: 'Active',
  username: '',
  registeredEmail: '',
  registeredPhone: '',
  password: '',
  mfaEnabled: false,
  mfaMethod: '',
  accessLevel: '',
  assignedTo: '',
  credentialExpiry: '',
  passwordLastUpdated: '',
  rotationRequired: false,
  createdBy: '',
  createdAt: '',
  updatedBy: '',
  updatedAt: '',
  lastAccessedAt: '',
};

const statusOptions = ['Active', 'Disabled', 'Archived'];
const mfaMethods = ['SMS', 'Email', 'Authenticator App'];
const accessLevels = ['Admin', 'Editor', 'Viewer'];

export default function SecureAccountsTable() {
  const dispatch = useDispatch();
  const { items: accounts, status, error } = useSelector((state) => state.secureAccounts);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const refreshAccounts = () => {
    dispatch(fetchSecureAccounts());
  };

  useEffect(() => {
    if (status === 'idle' && accounts.length === 0) {
      dispatch(fetchSecureAccounts());
    }
  }, [status, accounts.length, dispatch]);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editIndex, setEditIndex] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  // const [loading, setLoading] = useState(false); // Simulate loading for demo

  const decryptPassword = (encrypted) => {
    if (!encrypted) return '';
    try {
      const cleaned = String(encrypted).trim().replace(/\s+/g, '');
      const normalized = cleaned.replace(/-/g, '+').replace(/_/g, '/');
      return atob(normalized);
    } catch (error) {
      console.error('Failed to decrypt password:', error);
      return '';
    }
  };

  const handleCopyPassword = async (account, idx) => {
    const decrypted = decryptPassword(account.passwordEncrypted);
    const resolvedPassword =
      decrypted && decrypted !== '********' ? decrypted : (account._originalPassword || '');
    if (!resolvedPassword) {
      console.warn('Password unavailable for copy.');
      return;
    }
    try {
      await navigator.clipboard.writeText(resolvedPassword);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
    } catch (error) {
      console.error('Failed to copy password:', error);
    }
  };

  const handleViewOpen = (row) => {
    setViewRow(row);
    setViewOpen(true);
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setViewRow(null);
  };

  const handleAddOpen = () => {
    // Auto-fill credential expiry date with 3 months from today
    const today = new Date();
    const threeMonthsLater = new Date(today.setMonth(today.getMonth() + 3));
    const expiryDate = threeMonthsLater.toISOString().split('T')[0];
    
    setForm({ ...initialForm, credentialExpiry: expiryDate, secureAccountId: '' });
    setEditIndex(null);
    setFormError('');
    setOpenDialog(true);
  };

  const handleEditOpen = (account, idx) => {
    const id = account.secureAccountId || account.secureaccountid || account.id || account._id;
    console.log('Edit Account ID:', id, 'Account:', account);
    setForm({ ...account, secureAccountId: String(id || '') });
    setEditIndex(idx);
    setFormError('');
    setOpenDialog(true);
  };
  const handleDialogClose = () => {
    setOpenDialog(false);
    setForm(initialForm);
    setEditIndex(null);
    setFormError('');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (name === 'mfaEnabled' && !checked) {
      setForm((prev) => ({ ...prev, mfaMethod: '' }));
    }
  };

  // Filtered accounts for search
  const filteredAccounts = accounts.filter(
    (acc) =>
      (acc.appName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (acc.accountName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (acc.username || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ py: 3, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
              Secure Accounts
              <Chip
                label="Full Access"
                size="small"
                sx={{ ml: 2, bgcolor: '#dcfce7', color: '#15803d', fontWeight: 500 }}
              />
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage credentials, MFA, and access for sensitive accounts
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddOpen}
            sx={{ bgcolor: '#4B5AB6', '&:hover': { bgcolor: '#3a4891' }, borderRadius: 2 }}
          >
            Add Account
          </Button>
        </Box>

        {/* Search Bar */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <TextField
            fullWidth
            placeholder="Search accounts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: '#94a3b8', mr: 1 }} />,
            }}
            sx={{ bgcolor: '#fff', maxWidth: 400 }}
          />
        </Paper>

        {/* Table Section */}
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>App Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Secure ID</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Password</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>MFA</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Access Level</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Assigned To</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Credential Expiry</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {status === 'loading' ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ color: '#64748b', py: 6 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={32} />
                      <Typography variant="body2" color="text.secondary">
                        Loading secure accounts...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : status === 'failed' ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ color: '#b91c1c', py: 6 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Failed to load secure accounts
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {error || 'Something went wrong while fetching data.'}
                    </Typography>
                    <Button variant="outlined" onClick={refreshAccounts} size="small">
                      Retry
                    </Button>
                  </TableCell>
                </TableRow>
              ) : filteredAccounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ color: '#64748b', py: 6 }}>
                    <SearchIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
                      No Secure Accounts Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {searchTerm
                        ? `No accounts match your search criteria "${searchTerm}"`
                        : 'No secure accounts available yet.'}
                    </Typography>
                    <Button variant="outlined" onClick={() => setSearchTerm('')} size="small">
                      Clear Search
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAccounts.map((row, idx) => (
                  <TableRow
                    key={idx}
                    sx={{ '&:hover': { bgcolor: '#f8fafc' } }}
                  >
                    <TableCell>{row.appName}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{row.secureAccountId || '—'}</Typography>
                        {row.secureAccountId ? (
                          <Tooltip title={copiedIdx === `secure-id-${idx}` ? 'Copied!' : 'Copy secure ID'}>
                            <IconButton
                              size="small"
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(row.secureAccountId);
                                  setCopiedIdx(`secure-id-${idx}`);
                                  setTimeout(() => setCopiedIdx(null), 1500);
                                } catch (error) {
                                  console.error('Failed to copy secure ID:', error);
                                }
                              }}
                              sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}
                            >
                              <ContentCopyIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{row.username}</Typography>
                        {row.username ? (
                          <Tooltip title={copiedIdx === `username-${idx}` ? 'Copied!' : 'Copy username'}>
                            <IconButton
                              size="small"
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(row.username);
                                  setCopiedIdx(`username-${idx}`);
                                  setTimeout(() => setCopiedIdx(null), 1500);
                                } catch (error) {
                                  console.error('Failed to copy username:', error);
                                }
                              }}
                              sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}
                            >
                              <ContentCopyIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Box>
                    </TableCell>
                  
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{row.password}</Typography>
                        {row.passwordEncrypted || row._originalPassword ? (
                          <Tooltip title={copiedIdx === idx ? 'Copied!' : 'Copy decrypted password'}>
                            <IconButton
                              size="small"
                              onClick={() => handleCopyPassword(row, idx)}
                              sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}
                            >
                              <ContentCopyIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        size="small"
                        sx={{
                          bgcolor: row.status === 'Active' ? '#f0fdf4' : row.status === 'Disabled' ? '#fef2f2' : '#f3f4f6',
                          color: row.status === 'Active' ? '#15803d' : row.status === 'Disabled' ? '#dc2626' : '#64748b',
                          fontWeight: 500,
                          textTransform: 'capitalize'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {row.mfaEnabled ? (
                        <Chip label={row.mfaMethod} size="small" color="info" />
                      ) : (
                        <Typography variant="caption" color="text.secondary">No</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.accessLevel || 'N/A'}
                        size="small"
                        sx={{ bgcolor: '#e0e7ff', color: '#3730a3', fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>{row.assignedTo}</TableCell>
                    <TableCell>{row.credentialExpiry}</TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewOpen(row)}
                          sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Account">
                        <IconButton
                          size="small"
                          onClick={() => handleEditOpen(row, idx)}
                          sx={{ color: '#4B5AB6', '&:hover': { bgcolor: '#e0e7ff' } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={viewOpen} onClose={handleViewClose} maxWidth="sm" fullWidth>
          <DialogTitle>Secure Account Details</DialogTitle>
          <DialogContent dividers>
            {viewRow && (
              <Box sx={{ display: 'grid', gap: 1.2 }}>
                <Typography variant="body2"><strong>App Name:</strong> {viewRow.appName || '—'}</Typography>
                <Typography variant="body2"><strong>Secure ID:</strong> {viewRow.secureAccountId || '—'}</Typography>
                <Typography variant="body2"><strong>Username:</strong> {viewRow.username || '—'}</Typography>
                <Typography variant="body2"><strong>Email:</strong> {viewRow.registeredEmail || '—'}</Typography>
                <Typography variant="body2"><strong>Phone:</strong> {viewRow.registeredPhone || '—'}</Typography>
                <Typography variant="body2"><strong>Status:</strong> {viewRow.status || '—'}</Typography>
                <Typography variant="body2"><strong>Access Level:</strong> {viewRow.accessLevel || '—'}</Typography>
                <Typography variant="body2"><strong>Assigned To:</strong> {viewRow.assignedTo || '—'}</Typography>
                <Typography variant="body2"><strong>MFA:</strong> {viewRow.mfaEnabled ? (viewRow.mfaMethod || 'Enabled') : 'No'}</Typography>
                <Typography variant="body2"><strong>Credential Expiry:</strong> {viewRow.credentialExpiry || '—'}</Typography>
                <Divider />
                <Typography variant="body2"><strong>Notes:</strong> {viewRow.notes || '—'}</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleViewClose}>Close</Button>
          </DialogActions>
        </Dialog>

        <SecureAccountAddDialog
          open={openDialog}
          onClose={handleDialogClose}
          onSuccess={refreshAccounts}
          form={form}
          onChange={handleChange}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          formError={formError}
          editIndex={editIndex}
          isEditMode={editIndex !== null}
          statusOptions={statusOptions}
          mfaMethods={mfaMethods}
          accessLevels={accessLevels}
        />
      </Container>
    </Box>
  );
}
